import { useId, type ReactNode } from 'react';

import { cx } from '../utils/cx';

export interface RadioOption {
  readonly value: string;
  readonly label: string;
  readonly description?: string;
  readonly disabled?: boolean;
}

export interface RadioGroupProps {
  /** Visible group label. Rendered as the `<legend>` of the `<fieldset>`. */
  readonly legend: string;
  readonly name: string;
  readonly options: readonly RadioOption[];
  readonly value?: string;
  readonly defaultValue?: string;
  readonly disabled?: boolean;
  readonly onChange?: (value: string) => void;
}

/**
 * Radio group (§7.1 primitives).
 *
 * Exposed as a *group*, not as a lone `Radio`, because a single radio is
 * never valid on its own: the accessible name of the choice set lives on the
 * grouping element, and roving focus between options is a browser behaviour
 * that only exists when same-`name` inputs are siblings. `<fieldset>` +
 * `<legend>` is the native construct for that, so no ARIA is added on top.
 *
 * The card treatment (bordered, selected state tinted with `--ecom-accent-
 * soft`) is the prototypes' `.pay-option` pattern from the checkout payment
 * and shipping selectors, kept because Phase 3 checkout will need exactly
 * this control.
 */
export function RadioGroup({
  legend,
  name,
  options,
  value,
  defaultValue,
  disabled = false,
  onChange,
}: RadioGroupProps): ReactNode {
  const groupId = useId();

  return (
    <fieldset className="ecom-radio-group">
      <legend className="ecom-radio-group__legend">{legend}</legend>

      <div className="ecom-radio-group__options">
        {options.map((option) => {
          const optionId = `${groupId}-${option.value}`;
          const descriptionId = option.description !== undefined ? `${optionId}-desc` : undefined;
          const isDisabled = disabled || option.disabled === true;
          const isSelected = value !== undefined ? value === option.value : undefined;

          return (
            <div
              key={option.value}
              className={cx('ecom-choice', 'ecom-choice--card')}
              data-selected={isSelected}
            >
              <input
                id={optionId}
                type="radio"
                name={name}
                value={option.value}
                className={cx('ecom-choice__control', 'ecom-choice__control--radio')}
                {...(value !== undefined ? { checked: isSelected } : {})}
                {...(defaultValue !== undefined
                  ? { defaultChecked: defaultValue === option.value }
                  : {})}
                {...(descriptionId !== undefined ? { 'aria-describedby': descriptionId } : {})}
                disabled={isDisabled}
                onChange={() => onChange?.(option.value)}
              />
              <div className="ecom-choice__body">
                <label htmlFor={optionId} className="ecom-choice__label">
                  {option.label}
                </label>
                {option.description !== undefined ? (
                  <p id={descriptionId} className="ecom-choice__description">
                    {option.description}
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
